import { type DragEvent, type FormEvent, useId, useState } from 'react';

import { AttachButton, OwnFilesList } from '@/features/emails/OwnFiles';
import { attachmentsProblem, megabytes } from '@/features/emails/outgoing';
import { useHoldStrayDrops, useOwnFiles } from '@/features/emails/useOwnFiles';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input, Select } from '@/shared/ui/Input';
import { mailFailureReason } from '@/shared/emails/mailFailure';
import { TechnicalDetails } from '@/shared/ui/TechnicalDetails';
import { PaperclipIcon, RefreshIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { formatBytes, formatDate } from '@/shared/utils/format';

import { addressesFrom, notAnAddress, recipientsText, sendingState } from './sending';
import type { Quote, QuoteSendDefaults, QuoteSending } from './types';
import { useRetryAppend, useSendQuote } from './useQuotes';

const whenFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * «Enviar por correo» (contract v10.26): the quote's PDF to the customer, from a Cubas
 * mailbox, with the text around it, and a copy left in that mailbox's Sent folder so it
 * is in Thunderbird like anything else Víctor sends.
 *
 * Everything comes filled in from `send_defaults` (the customer's email, the subject with
 * the number, a short text signed by the mailbox) and everything but the sender and the
 * attachment can be changed: the plan says the recipient is editable, and a customer's
 * buyer is often not the address on the card. When the back already knows it would
 * refuse (`blocked_reason`: no outgoing server of its own, the mailbox is not registered),
 * the sheet says so and does not offer the button.
 *
 * A quote made by hand, or from an uploaded document, has no mailbox behind it (contract
 * v10.27): the back lists in `mailbox_choices` the ones this person can send from, with
 * the only one already chosen, and «Desde» becomes a choice when there are several.
 * Before, the sheet said «Desde .» and «Enviar» could only answer 422.
 *
 * Contract v10.59: other files can go after the PDF, as Víctor asked in the demo of 24/09
 * (a datasheet, a manual). The attachments of the supplier's email (`email_attachments`)
 * start unticked, because the supplier's own offer is usually one of them and it says what
 * Cubas pays; the person's own files go up as in the mail manager («Adjuntar» or dropped on
 * the sheet). A back without v10.59 sends no `max_attachment_bytes`, and then the sheet is
 * the one of before: the PDF alone.
 */
export function SendQuoteDialog({
  quote,
  defaults,
  onClose,
}: {
  quote: Quote;
  defaults: QuoteSendDefaults;
  onClose: () => void;
}) {
  const send = useSendQuote(quote.id);
  const [to, setTo] = useState((defaults.to ?? []).join(', '));
  const [cc, setCc] = useState((defaults.cc ?? []).join(', '));
  const [subject, setSubject] = useState(defaults.subject);
  const [body, setBody] = useState(defaults.body);
  const [mailbox, setMailbox] = useState(defaults.mailbox ?? '');
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  const [dragging, setDragging] = useState(false);
  const [tried, setTried] = useState(false);
  const limit = defaults.max_attachment_bytes ?? 0;
  const canAttach = limit > 0;
  const ownFiles = useOwnFiles(limit);
  const offered = canAttach ? (defaults.email_attachments ?? []) : [];
  const chosen = offered.filter((attachment) => ticked.has(attachment.id));
  const filesProblem = attachmentsProblem(
    {
      count: chosen.length,
      bytes: chosen.reduce((sum, attachment) => sum + attachment.size_bytes, 0),
    },
    ownFiles.files,
    limit,
  );
  const choices = defaults.mailbox_choices ?? [];
  const fromAddress =
    choices.find((choice) => choice.mailbox === mailbox)?.from_address ??
    defaults.from_address ??
    defaults.mailbox;
  const bodyId = useId();

  const toList = addressesFrom(to);
  const ccList = addressesFrom(cc);
  const badTo = notAnAddress(toList);
  const badCc = notAnAddress(ccList);
  const toError =
    toList.length === 0
      ? 'Pon al menos una dirección.'
      : toList.length > 10
        ? 'Como mucho diez direcciones.'
        : badTo
          ? `«${badTo}» no es una dirección de correo.`
          : undefined;
  const ccError =
    ccList.length > 10
      ? 'Como mucho diez direcciones.'
      : badCc
        ? `«${badCc}» no es una dirección de correo.`
        : undefined;
  const subjectError = subject.trim() ? undefined : 'El asunto no puede ir vacío.';
  const mailboxError = mailbox ? undefined : 'Elige desde qué buzón sale.';
  const valid = !toError && !ccError && !subjectError && !mailboxError && !filesProblem;
  const blocked = defaults.blocked_reason ?? null;
  useHoldStrayDrops();

  function toggle(attachmentId: string, on: boolean): void {
    setTicked((current) => {
      const next = new Set(current);
      if (on) next.add(attachmentId);
      else next.delete(attachmentId);
      return next;
    });
  }

  function attach(list: FileList | null): void {
    // While it goes, a new file would not be in what was sent, and the sheet closes.
    if (!canAttach || !list || send.isPending) return;
    ownFiles.add(list);
  }

  function carriesFiles(event: DragEvent): boolean {
    return canAttach && Array.from(event.dataTransfer.types).includes('Files');
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setTried(true);
    if (!valid || blocked || ownFiles.uploading) return;
    send.mutate(
      {
        to: toList,
        cc: ccList,
        subject: subject.trim(),
        body,
        ...(mailbox ? { mailbox } : {}),
        // In the order of the email, whatever order they were ticked in; only when there
        // are any, so a back before v10.59 gets what it always got.
        ...(chosen.length > 0 ? { attachment_ids: chosen.map((attachment) => attachment.id) } : {}),
        ...(ownFiles.ids.length > 0 ? { upload_ids: ownFiles.ids } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog
      title="Enviar por correo"
      description={quote.quote_number ?? undefined}
      size="lg"
      onClose={send.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form={`${bodyId}-form`}
            disabled={send.isPending || !!blocked || ownFiles.uploading}
          >
            {send.isPending ? 'Enviando…' : ownFiles.uploading ? 'Subiendo adjuntos…' : 'Enviar'}
          </Button>
        </>
      }
    >
      <form
        id={`${bodyId}-form`}
        className={cn(
          'flex flex-col gap-3 rounded-md',
          dragging && 'outline-accent outline-2 outline-offset-4 outline-dashed',
        )}
        onSubmit={submit}
        noValidate
        onDragOver={(event) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(event) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          setDragging(false);
          attach(event.dataTransfer.files);
        }}
      >
        {blocked ? (
          <Callout tone="warn" title="Desde aquí no se puede enviar todavía">
            {blocked} Mientras tanto, descarga el PDF y mándalo desde tu correo.
          </Callout>
        ) : null}
        {choices.length > 1 ? (
          <Select
            label="Desde"
            value={mailbox}
            onChange={(event) => setMailbox(event.target.value)}
            hint="Queda una copia en su carpeta de Enviados, como si lo mandaras desde Thunderbird."
            error={tried ? mailboxError : undefined}
          >
            <option value="">Elige un buzón…</option>
            {choices.map((choice) => (
              <option key={choice.mailbox} value={choice.mailbox}>
                {choice.from_address}
              </option>
            ))}
          </Select>
        ) : fromAddress ? (
          <p className="text-ink-2 text-[13.5px]">
            Desde <strong className="text-ink">{fromAddress}</strong>. Queda una copia en su carpeta
            de Enviados, como si lo mandaras desde Thunderbird.
          </p>
        ) : null}
        <Input
          label="Para"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          autoComplete="off"
          hint="Separa varias direcciones con comas."
          error={tried ? toError : undefined}
        />
        <Input
          label="Copia"
          optional
          value={cc}
          onChange={(event) => setCc(event.target.value)}
          autoComplete="off"
          error={tried ? ccError : undefined}
        />
        <Input
          label="Asunto"
          value={subject}
          maxLength={300}
          onChange={(event) => setSubject(event.target.value)}
          error={tried ? subjectError : undefined}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor={bodyId} className="text-ink-2 text-[13.5px] font-medium">
            Mensaje
          </label>
          <textarea
            id={bodyId}
            className="field h-auto min-h-40 py-2"
            rows={8}
            maxLength={10000}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </div>
        <p className="text-ink-3 flex items-center gap-1.5 text-[13px]">
          <PaperclipIcon size={14} />
          Adjunto: {defaults.attachment_name}
        </p>
        {offered.length > 0 ? (
          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-ink-2 mb-1 text-[13.5px] font-medium">
              Del correo del proveedor
            </legend>
            {offered.map((attachment) => (
              <label
                key={attachment.id}
                className="text-ink-2 flex flex-wrap items-center gap-2 text-[14px]"
              >
                <input
                  type="checkbox"
                  className="accent-accent size-4"
                  checked={ticked.has(attachment.id)}
                  onChange={(event) => toggle(attachment.id, event.target.checked)}
                  disabled={send.isPending}
                />
                <span className="min-w-0 break-all">{attachment.filename}</span>
                <span className="text-ink-4 text-[12.5px]">
                  {formatBytes(attachment.size_bytes)}
                </span>
              </label>
            ))}
            <p className="text-ink-3 text-[12.5px]">
              Marca solo lo que pueda ver el cliente, como una ficha técnica: la oferta del
              proveedor lleva sus precios.
            </p>
          </fieldset>
        ) : null}
        {canAttach ? (
          <div className="flex flex-col gap-1.5">
            <AttachButton
              onFiles={attach}
              disabled={send.isPending}
              hint={`O arrastra aquí los ficheros. Hasta ${megabytes(limit)} entre todos, sin contar el PDF.`}
            />
            <OwnFilesList
              files={ownFiles.files}
              onRemove={ownFiles.remove}
              disabled={send.isPending}
            />
            {filesProblem && (tried || !ownFiles.failed) ? (
              <p role="alert" className="text-danger text-[13px]">
                {filesProblem}
              </p>
            ) : null}
          </div>
        ) : null}
        {send.isError ? <ErrorMessage error={send.error}>No se pudo enviar</ErrorMessage> : null}
      </form>
    </Dialog>
  );
}

/** The sendings of a quote, newest first: where each one stands, and the copy in Sent. */
export function Sendings({ quote }: { quote: Quote }) {
  const retry = useRetryAppend(quote.id);
  const sendings = quote.sendings ?? [];
  if (sendings.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2" aria-label="Envíos por correo">
        {sendings.map((sending) => (
          <SendingLine
            key={sending.id}
            sending={sending}
            onRetry={() => retry.mutate(sending.id)}
            retrying={retry.isPending && retry.variables === sending.id}
          />
        ))}
      </ul>
      {retry.isError ? (
        <ErrorMessage error={retry.error}>No se pudo volver a guardar la copia</ErrorMessage>
      ) : null}
    </div>
  );
}

/** «el PDF, ficha.pdf y manual.pdf»: what went with the quote, the PDF always first. */
function alongsidePdf(names: string[]): string {
  const shown = names.slice(0, 3);
  const items = ['el PDF', ...shown];
  if (names.length > shown.length) items.push(`${names.length - shown.length} más`);
  return `${items.slice(0, -1).join(', ')} y ${items.at(-1)}`;
}

function SendingLine({
  sending,
  onRetry,
  retrying,
}: {
  sending: QuoteSending;
  onRetry: () => void;
  retrying: boolean;
}) {
  const state = sendingState(sending);
  const to = recipientsText(sending.to_addresses ?? []);
  if (state.kind === 'sending') {
    return (
      <li role="status" className="bg-paper-2 text-ink-2 rounded-md px-3 py-2 text-[13.5px]">
        Enviando a {to}… esta pantalla se actualiza sola.
      </li>
    );
  }
  if (state.kind === 'failed') {
    return (
      <li role="alert" className="bg-danger-soft text-danger rounded-md px-3 py-2 text-[13.5px]">
        <span className="font-medium">No se envió a {to}: </span>
        {mailFailureReason(state.reason)}.
        <TechnicalDetails text={state.reason} />
      </li>
    );
  }
  return (
    <li className="bg-ok-soft text-ok flex flex-col gap-1 rounded-md px-3 py-2 text-[13.5px]">
      <span>
        Enviado a {to}
        {(sending.cc_addresses ?? []).length > 0
          ? ` con copia a ${recipientsText(sending.cc_addresses ?? [])}`
          : ''}
        {sending.sent_at ? ` · ${formatDate(whenFormat, sending.sent_at)}` : ''}
      </span>
      {(sending.attachment_names ?? []).length > 0 ? (
        <span className="text-ink-3 text-[12.5px]">
          Con {alongsidePdf(sending.attachment_names ?? [])}.
        </span>
      ) : null}
      {state.copy === 'saved' ? (
        <span className="text-ink-3 text-[12.5px]">
          Copia en Enviados de {sending.from_address ?? sending.mailbox}.
        </span>
      ) : state.copy === 'saving' ? (
        <span className="text-ink-3 text-[12.5px]">Guardando la copia en Enviados…</span>
      ) : (
        <span className="text-warn flex flex-col items-start gap-1 text-[12.5px]">
          {state.copy === 'failed'
            ? 'El cliente lo tiene, pero no se guardó la copia en Enviados de tu correo.'
            : 'No consta la copia en Enviados de tu correo.'}
          {state.copyError ? <TechnicalDetails text={state.copyError} /> : null}
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshIcon size={14} />}
            onClick={onRetry}
            disabled={retrying}
          >
            {retrying ? 'Guardando…' : 'Reintentar la copia'}
          </Button>
        </span>
      )}
    </li>
  );
}
